import { useDocumentsWorker } from '@Contexts/documents';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePageImagesResult {
    pageImages: Map<number, string>;
    requestPage: (pageIndex: number) => void;
}

/**
 * Renders individual pages of the given (already compiled) document on demand and caches the
 * resulting object URLs. Switching to a different document token discards everything cached
 * for the previous one, and results for a since-superseded token (or after unmount) are dropped
 * when they arrive instead of being reported or cached.
 */
// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export function usePageImages(
    documentToken: number | undefined,
    pixelsPerPt: number,
    onError: (pageIndex: number, error: unknown) => void,
): UsePageImagesResult {
    const [pageImages, setPageImages] = useState<Map<number, string>>(new Map());
    const { renderPage } = useDocumentsWorker();

    const pageImagesRef = useRef<Map<number, string>>(new Map());
    const requestedPagesRef = useRef<Set<number>>(new Set());
    const currentDocumentTokenRef = useRef(documentToken);
    currentDocumentTokenRef.current = documentToken;
    // Sibling to the documentToken check below: a render can also arrive after this hook has
    // already unmounted, when the documentToken prop hasn't changed since (nothing re-renders it).
    const isUnmountedRef = useRef(false);

    const clearPageImages = useCallback(() => {
        for (const url of pageImagesRef.current.values()) {
            URL.revokeObjectURL(url);
        }
        pageImagesRef.current = new Map();
        setPageImages(new Map());
    }, []);

    // A new (or superseded) document invalidates every page already rendered for the previous one.
    useEffect(() => {
        requestedPagesRef.current = new Set();
        clearPageImages();
    }, [documentToken, clearPageImages]);

    useEffect(() => {
        // Reset on every (re)mount, not just once: React StrictMode (and any real remount that
        // reuses this instance) runs the cleanup below before re-running this effect, which would
        // otherwise leave isUnmountedRef stuck at true and make isStale() drop every page render.
        isUnmountedRef.current = false;
        return () => {
            isUnmountedRef.current = true;
            clearPageImages();
        };
    }, [clearPageImages]);

    const requestPage = useCallback(
        (pageIndex: number) => {
            if (documentToken === undefined || requestedPagesRef.current.has(pageIndex)) {
                return;
            }
            requestedPagesRef.current.add(pageIndex);

            const isStale = () => isUnmountedRef.current || currentDocumentTokenRef.current !== documentToken;

            renderPage({ documentToken, pageIndex, pixelsPerPt })
                .then((renderResult) => {
                    // Drop pages that belong to a document superseded (or unmounted) in the meantime.
                    if (renderResult === undefined || isStale()) {
                        return;
                    }
                    const previous = pageImagesRef.current.get(pageIndex);
                    if (previous !== undefined) {
                        URL.revokeObjectURL(previous);
                    }
                    const next = new Map(pageImagesRef.current);
                    next.set(pageIndex, URL.createObjectURL(renderResult.result));
                    pageImagesRef.current = next;
                    setPageImages(next);
                })
                .catch((error) => {
                    if (!isStale()) {
                        onError(pageIndex, error);
                    }
                });
        },
        [documentToken, pixelsPerPt, renderPage, onError],
    );

    return { pageImages, requestPage };
}
